import { bind, classNames, h, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { fireEvent, TrackEvents } from "@/components/nw-track/events";
import { installEditor } from "@/editor/install";
import { PureComponent } from "@/utils/pureComponent";
import { Track } from "@/api";
import { NWDivider, NWEditorSkeleton, NWLoadingSpinner, NWSuspense, NWTooltip, openProModal } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import globalStyles from "../../globals/styles.css?inline";

type Props = {
  tracks: Track[];
  selected: Track["id"];
};

@tag("nw-track-manager")
export class NWTrackManager extends PureComponent<Props> {
  static css = [tailwind, globalStyles];
  static propTypes = {
    selected: String,
  };

  @bind
  private async selectTrack(trackId: Required<Track["id"]>) {
    if (Store.project.isProProject() && !Store.project.isProProjectUnlockedForUser()) {
      openProModal("pro_project_unlock");
      return;
    }

    if (isEmpty(trackId)) {
      return;
    }

    const currentTrackId = Store.project.getSelectedTrackId();
    if (trackId == currentTrackId && !isEmpty(Store.project.content.trackContent.signal.data.value)) {
      return;
    }

    fireEvent(TrackEvents.TRACK_SELECTED, { id: trackId! });
    Store.project.content.setSelectedTrack(trackId);
    Store.project.content.fetchTrackContent(trackId);
  }

  render({ tracks }: Props) {
    const isEditorEnabled = Store.features.hasFeature("projects.editor");
    const selectedTrack = Store.project.getSelectedTrackId();
    const trackContentSignal = Store.project.content.trackContent.signal;

    return (
      <div class="w-full">
        {tracks.length > 1 && (
          <div className={classNames("sm:max-w-[880px] px-12", { "mb-[32rem]": !selectedTrack })}>
            <NWDivider padding="sm" casing="normal-case">
              <div class="flex overflow-hidden justify-start items-start rounded-lg border divide-x text-3">
                {tracks.map(({ id, title, description }) => (
                  <div>
                    <NWTooltip placement="bottom" content={description ?? ""}>
                      <button
                        class={classNames(
                          "flex min-h-10 py-1 px-4 justify-center items-center text-tertiary gap-2 break-words hover:bg-secondary-hover transition-colors active:bg-secondary",
                          { "bg-brand-25": selectedTrack === id },
                        )}
                        onClick={() => this.selectTrack(id)}
                      >
                        <div>
                          <img src={`/static/${id}-touch.png`} width={24} height={24} />
                        </div>
                        <span class="font-semibold">{title}</span>
                      </button>
                    </NWTooltip>
                  </div>
                ))}
              </div>
            </NWDivider>
          </div>
        )}
        {isEditorEnabled && (
          <NWSuspense
            loadFunc={async () => {
              const NWEditor = await installEditor();
              return { NWEditor };
            }}
            loadingComponent={() => <NWEditorSkeleton />}
            onLoadComplete={({ NWEditor }) => (
              <>
                {trackContentSignal.isLoading.value && <NWEditorSkeleton />}
                {trackContentSignal.data.value && (
                  <NWEditor
                    instanceId="content"
                    content={trackContentSignal.data.value}
                    saveAgainstTrack
                    withScrollObservation
                    withNodeHover
                  />
                )}
              </>
            )}
          />
        )}

        {!isEditorEnabled && (
          <>
            {trackContentSignal.isLoading.value && <NWLoadingSpinner />}
            {trackContentSignal.data.value && <p unsafeHTML={{ html: trackContentSignal.data.value }}></p>}
          </>
        )}

        <slot id="tracks-slot"></slot>
      </div>
    );
  }
}
